import { OutreachEditorTokenKey } from '@recruit-robin/robin-theme/web-components';
import { useContext, useMemo } from 'react';
import { useContextSelector } from 'use-context-selector';

import type { Candidate } from 'model/Candidate/Candidate';
import type {
  CandidateContextProps,
  OutreachInformationWhyNotLabel,
  OutreachInformationWhyNotLabels,
  OutreachMethods,
} from '../types';

import { jobBoardLabel } from 'helpers/jobBoards';
import { useIsWerknlActive } from 'hooks/search/useIsWerknlActive';
import { useBrowserExtensionStatusQuery } from 'hooks/shared/useBrowserExtensionStatusQuery';
import {
  useResolveCommAgentFlow,
  useResolveRjCommHubChannelEnabled,
} from 'shared/legacy/contexts/CommAgentFlowContext';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { useFiltersContext } from 'shared/legacy/contexts/SearchContext/FiltersContext/helpers';
import { SelectedCampaignContext } from 'shared/legacy/contexts/SelectedCampaignContext/SelectedCampaignContext.context';
import { useIndeedConfig } from 'shared/legacy/hooks/useBrowserExtension/indeed/useIndeedConfig';
import { useLinkedinConfig } from 'shared/legacy/hooks/useBrowserExtension/linkedin/useLinkedinConfig';
import { useLinkedinInMailConfig } from 'shared/legacy/hooks/useBrowserExtension/linkedinInMail/useLinkedinInMailConfig';
import { useCurrentUserQuery } from 'shared/legacy/hooks/useCurrentUserQuery';
import useCvSources from 'shared/legacy/hooks/useCvSources';
import { DISABLED_OUTREACH_ERROR } from './errors';

const RESTRICTED_CHANNEL_TO_OUTREACH_KEY: Partial<Record<string, keyof OutreachMethods>> = {
  email: 'shouldEnableEmail',
  whatsapp: 'shouldEnableWhatsapp',
  call: 'shouldEnableCall',
  linkedin: 'shouldEnableLinkedin',
  linkedin_inmail: 'shouldEnableLinkedinPro',
  indeed: 'shouldEnableIndeed',
  werknl: 'shouldEnableWerknl',
  rj_communication_hub: 'shouldEnableRJCommHub',
};

export const useOutreach: UseOutreachHook = (candidate, options) => {
  const { dictionary } = useContext(LocalizationContext);
  const selectedCampaign = useContextSelector(SelectedCampaignContext, (state) => state.campaign);
  const [locations] = useFiltersContext('locations');
  const { data: currentUser } = useCurrentUserQuery();
  const browserExtensionStatusQuery = useBrowserExtensionStatusQuery();
  const { status: linkedinStatus, isLoggedIn: isLinkedinLoggedIn } = useLinkedinConfig();
  const { status: linkedinInMailStatus, isLoggedIn: isLinkedinInMailLoggedIn } = useLinkedinInMailConfig();
  const { status: indeedStatus, isLoggedIn: isIndeedLoggedIn } = useIndeedConfig();
  const { data: isWerknlActive } = useIsWerknlActive();
  const { enabledCvSources } = useCvSources();
  const commAgentFlow = useResolveCommAgentFlow(candidate);
  const isRjCommHubChannelEnabled = useResolveRjCommHubChannelEnabled(candidate);

  const disabledMessage: OutreachInformationWhyNotLabels = useMemo(() => {
    return {
      unavailableOnThisContactMethod: {
        label: dictionary.unavailableOnThisContactMethod,
      },
      browserExtensionNotInstalled: {
        label: dictionary.browserExtensionNotInstalled,
      },
      needsLoginOnJobboard: {
        label: dictionary.needsLoginOnJobboard,
      },
    };
  }, [dictionary]);

  const outreachInformationWhyNotEnabled: CandidateContextProps['outreachInformationWhyNotEnabled'] = useMemo(() => {
    const disabledError: CandidateContextProps['outreachInformationWhyNotEnabled']['disabledError'] = {
      linkedin: undefined,
      linkedinPro: undefined,
      indeed: undefined,
      email: undefined,
      whatsapp: undefined,
      call: undefined,
      werknl: undefined,
      rjCommHub: undefined,
    };
    let whyLinkedinNotEnabled = undefined;
    let whyLinkedinProNotEnabled = undefined;
    let whyIndeedNotEnabled = undefined;
    let whyEmailNotEnabled = undefined;
    let whyWhatsappNotEnabled = undefined;
    let whyCallNotEnabled = undefined;
    let whyWerknlNotEnabled = undefined;
    let whyRJCommHubNotEnabled = undefined;
    // Check browser extensions
    if (Object.keys(browserExtensionStatusQuery.data).length === 0) {
      disabledError.linkedin = DISABLED_OUTREACH_ERROR.BROWSER_EXTENSION_NOT_INSTALLED;
      disabledError.indeed = DISABLED_OUTREACH_ERROR.BROWSER_EXTENSION_NOT_INSTALLED;

      whyLinkedinNotEnabled = disabledMessage.browserExtensionNotInstalled;
      whyIndeedNotEnabled = disabledMessage.browserExtensionNotInstalled;
    }

    // Needs login
    if (linkedinStatus === 'Logged Out') {
      disabledError.linkedin = DISABLED_OUTREACH_ERROR.LOGGED_OUT;
      whyLinkedinNotEnabled = { label: dictionary.clickToActivatePlatform(dictionary.linkedIn) };
    }

    if (linkedinInMailStatus === 'Logged Out') {
      disabledError.linkedin = DISABLED_OUTREACH_ERROR.LOGGED_OUT;
      whyLinkedinProNotEnabled = { label: dictionary.clickToActivatePlatform(dictionary.linkedInInMail) };
    }

    if (indeedStatus === 'Logged Out') {
      disabledError.indeed = DISABLED_OUTREACH_ERROR.LOGGED_OUT;
      whyIndeedNotEnabled = { label: dictionary.clickToActivatePlatform(dictionary.indeed) };
    }

    if (candidate?.activeJobBoard !== 'werknl' || !isWerknlActive.isActive) {
      disabledError.werknl = DISABLED_OUTREACH_ERROR.UNAVAILABLE;
      whyWerknlNotEnabled = { label: dictionary.clickToActivatePlatform(dictionary.Werknl) };
    }

    // Unavailable on this contact method
    if (!candidate?.contact_info.email) {
      disabledError.email = DISABLED_OUTREACH_ERROR.UNAVAILABLE;
      whyEmailNotEnabled = disabledMessage.unavailableOnThisContactMethod;
    }

    if (!candidate?.contact_info.phone) {
      disabledError.whatsapp = DISABLED_OUTREACH_ERROR.UNAVAILABLE;
      whyWhatsappNotEnabled = disabledMessage.unavailableOnThisContactMethod;
    }

    if (candidate?.activeJobBoard !== 'cvt') {
      disabledError.linkedin = DISABLED_OUTREACH_ERROR.UNAVAILABLE;
      whyLinkedinNotEnabled = disabledMessage.unavailableOnThisContactMethod;
    }

    if (candidate?.activeJobBoard !== 'indeed') {
      disabledError.indeed = DISABLED_OUTREACH_ERROR.UNAVAILABLE;
      whyIndeedNotEnabled = disabledMessage.unavailableOnThisContactMethod;
    }

    if (!candidate?.contact_info.phone) {
      disabledError.call = DISABLED_OUTREACH_ERROR.UNAVAILABLE;
      whyCallNotEnabled = disabledMessage.unavailableOnThisContactMethod;
    }

    if (candidate?.communicationOptOut) {
      disabledError.rjCommHub = DISABLED_OUTREACH_ERROR.UNAVAILABLE;
      whyRJCommHubNotEnabled = { label: dictionary.cantUseRobinJrUnsubscribed };
    }

    return {
      disabledError,
      whyEmailNotEnabled,
      whyWhatsappNotEnabled,
      whyLinkedinNotEnabled,
      whyLinkedinProNotEnabled,
      whyIndeedNotEnabled,
      whyCallNotEnabled,
      whyWerknlNotEnabled,
      whyRJCommHubNotEnabled,
    };
  }, [
    browserExtensionStatusQuery.data,
    linkedinStatus,
    linkedinInMailStatus,
    indeedStatus,
    candidate?.contact_info.email,
    candidate?.contact_info.phone,
    candidate?.activeJobBoard,
    candidate?.communicationOptOut,
    dictionary,
    isWerknlActive,
  ]);

  const outreachMethods: CandidateContextProps['outreachMethods'] = useMemo(() => {
    const shouldEnableRjCommHub = isRjCommHubChannelEnabled;

    const enabledChannels: OutreachMethods = {
      shouldEnableEmail: !!candidate?.contact_info.email,
      shouldEnableIndeed: candidate?.activeJobBoard === 'indeed' && enabledCvSources?.includes('indeed'),
      shouldEnableWhatsapp: !!candidate?.contact_info.phone,
      shouldEnableLinkedin:
        candidate?.activeJobBoard === 'cvt' &&
        (enabledCvSources?.includes('linkedin') || enabledCvSources?.includes('cvt')),
      shouldEnableLinkedinPro: linkedinInMailStatus === 'Logged In' && candidate?.activeJobBoard === 'cvt',
      shouldEnableWerknl: false,
      shouldEnableCall: !!candidate?.contact_info.phone,
      shouldEnableRJCommHub: shouldEnableRjCommHub,
    };

    const shouldEnableWerknl =
      candidate?.activeJobBoard === 'werknl' &&
      enabledCvSources?.includes('werknl') &&
      !Object.values(enabledChannels).some((e) => e);
    enabledChannels['shouldEnableWerknl'] = shouldEnableWerknl;

    const restricted = { ...enabledChannels };
    const restrictedChannels = candidate?.restrictOutreachChannels ?? [];
    for (const channel of restrictedChannels) {
      const key = RESTRICTED_CHANNEL_TO_OUTREACH_KEY[channel];
      if (key) restricted[key] = false;
    }
    return restricted;
  }, [
    candidate?.contact_info.email,
    candidate?.contact_info.phone,
    candidate?.activeJobBoard,
    candidate?.status,
    candidate?.restrictOutreachChannels,
    indeedStatus,
    linkedinStatus,
    isWerknlActive,
    enabledCvSources,
    isRjCommHubChannelEnabled,
  ]);

  const isOutreachMethodConnected: CandidateContextProps['outreachMethods'] = useMemo(() => {
    return {
      ...outreachMethods,
      shouldEnableIndeed: isIndeedLoggedIn,
      shouldEnableLinkedin: isLinkedinLoggedIn,
      shouldEnableLinkedinPro: isLinkedinInMailLoggedIn,
      shouldEnableWerknl: isWerknlActive.isActive,
      shouldEnableRJCommHub: commAgentFlow !== null,
    };
  }, [outreachMethods, isIndeedLoggedIn, isLinkedinLoggedIn, isLinkedinInMailLoggedIn, isWerknlActive, commAgentFlow]);

  const availableOutreachMethods: Array<keyof OutreachMethods> = useMemo(() => {
    const availableMethods = Object.entries(outreachMethods)
      .filter(([, value]) => value === true)
      .map(([key]) => key as keyof OutreachMethods)
      .sort((a, b) => (isOutreachMethodConnected[b] ? 1 : 0) - (isOutreachMethodConnected[a] ? 1 : 0));

    return availableMethods;
  }, [outreachMethods, isOutreachMethodConnected]);

  const personalizationTokens: CandidateContextProps['personalizationTokens'] = useMemo(
    () => ({
      '{firstName}': candidate?.firstName || '',
      '{lastName}': candidate?.lastName || '',
      '{fullName}': [candidate?.firstName, candidate?.lastName].filter(Boolean).join(' ') || '',
      '{lastCompany}': candidate?.lastThreePositions?.[0]?.companyName || '',
      '{lastPosition}': candidate?.lastThreePositions?.[0]?.positionName || '',
      '{recruiterFullName}': [currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(' '),
      '{nameJobboard}': candidate?.activeJobBoard ? jobBoardLabel(candidate.activeJobBoard) : '',
      '{nameVacancy}': selectedCampaign?.name || '',
      '{location}': locations.filter(Boolean) ? locations?.map((value) => value.key).join(', ') : '',
      '{schedulerLink}': options?.schedulerLink ?? '',
    }),
    [
      candidate?.firstName,
      candidate?.lastName,
      candidate?.lastThreePositions?.[0],
      currentUser?.first_name,
      currentUser?.last_name,
      options?.schedulerLink,
    ],
  );

  return useMemo(
    () => ({
      outreachMethods,
      personalizationTokens,
      outreachInformationWhyNotEnabled,
      availableOutreachMethods,
      isOutreachMethodConnected,
    }),
    [
      JSON.stringify(outreachMethods),
      JSON.stringify(availableOutreachMethods),
      JSON.stringify(personalizationTokens),
      JSON.stringify(outreachInformationWhyNotEnabled),
    ],
  );
};

type UseOutreachHook = (
  candidate: Candidate | undefined,
  options?: { schedulerLink?: string },
) => {
  outreachMethods: OutreachMethods;
  isOutreachMethodConnected: CandidateContextProps['outreachMethods'];
  availableOutreachMethods: Array<keyof OutreachMethods>;
  personalizationTokens: Record<OutreachEditorTokenKey, string>;
  outreachInformationWhyNotEnabled: {
    whyLinkedinNotEnabled: OutreachInformationWhyNotLabel | undefined;
    whyLinkedinProNotEnabled: OutreachInformationWhyNotLabel | undefined;
    whyIndeedNotEnabled: OutreachInformationWhyNotLabel | undefined;
    whyEmailNotEnabled: OutreachInformationWhyNotLabel | undefined;
    whyWhatsappNotEnabled: OutreachInformationWhyNotLabel | undefined;
    whyCallNotEnabled: OutreachInformationWhyNotLabel | undefined;
    whyWerknlNotEnabled: OutreachInformationWhyNotLabel | undefined;
    whyRJCommHubNotEnabled: OutreachInformationWhyNotLabel | undefined;
    disabledError: CandidateContextProps['outreachInformationWhyNotEnabled']['disabledError'];
  };
};
